/**
 * @fileoverview Compact 4-tile KPI strip for the Banking module's page-opening metrics.
 * @description One shared bordered/shadowed container holding every tile (a dark navy "headline"
 * tile plus plain ones), rather than each metric getting its own individually-bordered KpiCard —
 * ported from the InsightBox Banking design system's own `KpiStrip`. Distinct from the
 * sales-module `KpiStrip` (grid + divider lines, no trend/loading support): that component already
 * has 7 real Sales-page call sites on its current simple `{label, value, tone}` shape, so this one
 * is its own component rather than risking a visual regression there by generalizing it in place.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Skeleton from '@/shared/components/atoms/Skeleton'

interface BankingKpiStripTrend {
  direction: 'up' | 'down' | 'flat'
  /** Whether "up" is the favorable direction — flips the arrow's color for a metric where a
   * decrease is the good outcome (e.g. NPA%). Defaults to true. */
  positiveIsGood?: boolean
}

export interface BankingKpiStripItem {
  label: string
  value: string
  sublabel?: string
  /** Tints this one tile dark navy to keep a visual "primary metric" anchor. */
  highlight?: boolean
  trend?: BankingKpiStripTrend
}

interface BankingKpiStripProps {
  items: BankingKpiStripItem[]
  loading?: boolean
}

const TREND_ICON_PATH: Record<BankingKpiStripTrend['direction'], string> = {
  up: 'M12 19V5M5 12l7-7 7 7',
  down: 'M12 5v14M5 12l7 7 7-7',
  flat: 'M5 12h14',
}

/**
 * @description The small trend arrow next to a tile's value — color follows whether the direction
 * is actually favorable, matching KpiCard's own TrendRow convention.
 * @param {BankingKpiStripTrend} trend - The direction/favorability to render.
 */
function TrendArrow(trend: BankingKpiStripTrend) {
  const trendGood =
    trend.direction === 'flat'
      ? null
      : (trend.direction === 'up') === (trend.positiveIsGood ?? true)

  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={trendGood === null ? '' : trendGood ? 'text-success' : 'text-error'}
      aria-hidden="true"
    >
      <path d={TREND_ICON_PATH[trend.direction]} />
    </svg>
  )
}

/**
 * @component
 * @param {BankingKpiStripProps} props - The strip's tiles and loading state.
 * @param {BankingKpiStripItem[]} props.items - The label/value/sublabel/trend tiles, in display
 * order — usually 4, with the primary metric's `highlight` set.
 * @param {boolean} [props.loading] - Shows a shimmer in place of every value/sublabel, keeping the
 * labels and strip frame in place so layout doesn't jump. Off by default.
 */
function BankingKpiStrip({ items, loading = false }: BankingKpiStripProps) {
  return (
    <div className="flex shrink-0 flex-wrap gap-2 rounded-2xl border border-border bg-white p-2 shadow-card">
      {items.map((item, index) => (
        <div
          key={`${item.label}-${index}`}
          className={`flex min-w-[150px] flex-1 flex-col justify-center gap-0.5 rounded-xl px-3.5 py-2.5 ${
            item.highlight ? 'bg-accent' : 'bg-input-bg'
          }`}
        >
          <p
            className={`truncate text-[10px] font-semibold tracking-wide uppercase ${
              item.highlight ? 'text-white/70' : 'text-label'
            }`}
          >
            {item.label}
          </p>
          {loading ? (
            <Skeleton height={20} width={64} />
          ) : (
            <p
              className={`flex items-center gap-1 truncate text-lg font-extrabold ${
                item.highlight ? 'text-white' : 'text-text'
              }`}
            >
              {item.value}
              {item.trend && item.trend.direction !== 'flat' && <TrendArrow {...item.trend} />}
            </p>
          )}
          {loading ? (
            <Skeleton height={10} width={48} />
          ) : (
            item.sublabel && (
              <p
                className={`truncate text-[10px] font-medium ${
                  item.highlight ? 'text-white/60' : 'text-label'
                }`}
              >
                {item.sublabel}
              </p>
            )
          )}
        </div>
      ))}
    </div>
  )
}

export default BankingKpiStrip
